import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../lib/AuthContext";
import { trackEvent } from "../lib/analytics";
import {
  UploadSimple, FileText, CircleNotch, CheckCircle, ShieldWarning,
  ShieldCheck, DownloadSimple, ArrowRight, ArrowLeft, Trash, Info,
  Warning, ListChecks, Lock, Crown, MagnifyingGlass, Funnel, Eye,
  X, CaretUp, CaretDown, Broadcast, Users, MapPin, Pulse,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import Breadcrumb from "../components/Breadcrumb";
import { softwareAppSchema, faqPageSchema, breadcrumbSchema } from "../lib/jsonLd";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BulkScreening = () => {
  const { user, isAuthenticated, loading: authLoading, login } = useAuth();
  const [inputMode, setInputMode] = useState("paste");
  const [pasteText, setPasteText] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobMeta, setJobMeta] = useState(null);
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("");
  const [pecosFilter, setPecosFilter] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  const [sortBy, setSortBy] = useState("npi");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);

  const tier = user?.tier || "free";
  const bulkLimit = user?.tier_config?.bulk_limit || 0;
  const canBulk = bulkLimit !== 0;

  const pastedNpis = pasteText.trim()
    ? pasteText.split(/[\n,\s]+/).filter(t => t.trim().replace(/\D/g, "").length === 10)
    : [];

  // Fetch results with filters
  const fetchResults = useCallback(async (jid) => {
    if (!jid) return;
    try {
      const params = new URLSearchParams({
        page, per_page: perPage, search, status_filter: statusFilter,
        entity_filter: entityFilter, state_filter: stateFilter,
        pecos_filter: pecosFilter, risk_filter: riskFilter,
        sort_by: sortBy, sort_dir: sortDir,
      });
      const res = await fetch(`${API_URL}/api/bulk-screening/results/${jid}?${params}`);
      if (res.ok) {
        const d = await res.json();
        setResults(d.results);
        setSummary(d.summary);
        setTotalFiltered(d.total);
        setTotalPages(d.total_pages);
        setJobMeta(prev => ({ ...prev, status: d.job_status, processed: d.processed, total: d.job_total }));
      }
    } catch (e) { console.error(e); }
  }, [page, perPage, search, statusFilter, entityFilter, stateFilter, pecosFilter, riskFilter, sortBy, sortDir]);

  // Poll job + results
  const startPolling = useCallback((jid) => {
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/bulk-screening/status/${jid}`);
        if (res.ok) {
          const d = await res.json();
          setJobMeta(d);
          if (d.status === "complete") {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch { /* ignore */ }
      fetchResults(jid);
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
  }, [fetchResults]);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Re-fetch when filters change (only if job is complete)
  useEffect(() => {
    if (jobId && jobMeta?.status === "complete") fetchResults(jobId);
  }, [jobId, jobMeta?.status, fetchResults]);

  const handleFileDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (f) {
      if (!f.name.endsWith(".csv")) { setError("Please upload a CSV file."); return; }
      setFile(f); setError(null);
    }
  };

  const handleSubmit = async () => {
    setUploading(true); setError(null); setJobId(null); setJobMeta(null); setResults([]); setSummary(null);
    try {
      let res;
      if (inputMode === "paste") {
        if (!pasteText.trim()) { setError("No NPIs entered."); setUploading(false); return; }
        res = await fetch(`${API_URL}/api/bulk-screening/submit-text`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: pasteText }),
        });
      } else {
        if (!file) { setError("No file selected."); setUploading(false); return; }
        const fd = new FormData(); fd.append("file", file);
        res = await fetch(`${API_URL}/api/bulk-screening/upload`, { method: "POST", body: fd });
      }
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Submission failed."); setUploading(false); return; }
      trackEvent("bulk_screening_create", { source: inputMode, npi_count: data.total_npis || 0, public: true });
      setJobId(data.job_id);
      setJobMeta({ status: "processing", total: data.total_npis, processed: 0,
        total_submitted: data.total_submitted, invalid_count: data.invalid_count, dupe_count: data.dupe_count });
      startPolling(data.job_id);
    } catch { setError("Submission failed."); }
    finally { setUploading(false); }
  };

  const handleReset = () => {
    setFile(null); setPasteText(""); setJobId(null); setJobMeta(null);
    setResults([]); setSummary(null); setError(null);
    setSearch(""); setStatusFilter("all"); setEntityFilter("all");
    setStateFilter(""); setPecosFilter("all"); setRiskFilter("all");
    setPage(1);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
    setPage(1);
  };

  const isComplete = jobMeta?.status === "complete";
  const isProcessing = jobMeta?.status === "processing";
  const progress = jobMeta ? Math.round((jobMeta.processed / Math.max(jobMeta.total, 1)) * 100) : 0;

  const hasActiveFilters = search || statusFilter !== "all" || entityFilter !== "all" || stateFilter || pecosFilter !== "all" || riskFilter !== "all";

  return (
    <>
      <Helmet>
        <title>Bulk NPI Lookup — PECOS + OIG Registry Screening | NPI Lookup</title>
        <meta name="description" content="Bulk NPI lookup tool — screen up to 5,000 NPI numbers against the NPPES registry, PECOS Medicare enrollment, and the OIG exclusion database in minutes." />
        <link rel="canonical" href="https://npilookup.com/bulk-screening" />
        <script type="application/ld+json">{JSON.stringify(softwareAppSchema({
          name: "Bulk NPI Compliance Screening",
          description: "Upload or paste up to 5,000 NPI numbers and screen them against NPPES, PECOS Medicare enrollment, and the OIG exclusion list in a single run. Credentialing-grade compliance reports for healthcare staffing, MSOs, and payors.",
          url: "/bulk-screening",
          category: "BusinessApplication",
        }))}</script>
        <script type="application/ld+json">{JSON.stringify(faqPageSchema([
          { q: "What is bulk NPI screening?", a: "Bulk NPI screening checks a list of healthcare provider NPI numbers against the NPPES national registry, the PECOS Medicare enrollment database, and the OIG exclusion list to verify each provider is registered, Medicare-enrolled, and not excluded from federal healthcare programs." },
          { q: "How many NPIs can I screen at once?", a: "Free plans include 2 bulk runs per day with up to 25 NPIs each. Paid plans scale up to 2,500 NPIs per run and 25 runs per day." },
          { q: "Which data sources are checked?", a: "Each NPI is verified against CMS NPPES (registration), CMS PECOS (Medicare enrollment), and the HHS Office of Inspector General's LEIE exclusion list — the three datasets required for Medicare/Medicaid credentialing compliance." },
          { q: "Can I re-run a bulk job later?", a: "Yes. Saved jobs can be re-run on demand; the system diffs the new result against the prior run and highlights status changes (new OIG exclusions, PECOS re-validations, etc.)." },
        ]))}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Bulk Screening" }]))}</script>
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="relative bg-navy overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }} />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-3">
                <ListChecks className="w-4 h-4 text-teal" weight="fill" />
                <span className="text-teal text-sm font-medium">Operational Screening Tool</span>
              </div>
              <h1 className="font-display font-bold text-3xl sm:text-4xl text-white mb-2" data-testid="bulk-title">
                Bulk Compliance Screening
              </h1>
              <p className="text-slate-300 text-base max-w-xl mx-auto">
                Screen providers for NPI status, Medicare enrollment, OIG exclusions, and risk flags. Paste NPIs or upload a CSV.
              </p>
            </div>
          </div>
        </section>

        {/* Main */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Breadcrumb items={[{ name: "Home", to: "/" }, { name: "Bulk Screening" }]} />
          {/* Gate */}
          {!authLoading && (!isAuthenticated || !canBulk) ? (
            <div className="max-w-xl mx-auto bg-white rounded-2xl border-2 border-slate-200 p-12 text-center" data-testid="bulk-locked">
              <Lock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h2 className="font-display font-bold text-xl text-slate-900 mb-2">Bulk Screening requires a Pro plan</h2>
              <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">Screen up to 100 NPIs (Pro) or 5,000 NPIs (Team/Enterprise) with full enrichment, risk flags, and downloadable reports.</p>
              {!isAuthenticated ? (
                <Button onClick={() => login({ appState: { returnTo: "/bulk-screening" } })} className="bg-teal hover:bg-teal/90 text-white rounded-xl gap-2 h-11 px-6" data-testid="bulk-login-btn">Sign In to Get Started</Button>
              ) : (
                <Link to="/pricing"><Button className="bg-teal hover:bg-teal/90 text-white rounded-xl gap-2 h-11 px-6" data-testid="bulk-upgrade-btn"><Crown className="w-4 h-4" weight="fill" /> Upgrade to Pro</Button></Link>
              )}
            </div>
          ) : (
          <>
            {/* Input area — only show if no active job */}
            {!jobId && (
              <div className="max-w-3xl mx-auto space-y-4 mb-8">
                <div className="flex items-center bg-slate-100 rounded-xl p-1 w-fit">
                  <button onClick={() => setInputMode("paste")} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${inputMode === "paste" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Paste NPIs</button>
                  <button onClick={() => setInputMode("upload")} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${inputMode === "upload" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Upload CSV</button>
                </div>

                {inputMode === "paste" ? (
                  <div>
                    <textarea value={pasteText} onChange={e => setPasteText(e.target.value)}
                      placeholder="Paste NPI numbers — one per line, comma-separated, or space-separated..." rows={6}
                      className="w-full text-sm font-mono border-2 border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal/50 bg-slate-50" data-testid="paste-textarea" />
                    {pastedNpis.length > 0 && <p className="text-sm text-teal font-medium mt-1">{pastedNpis.length} valid NPI{pastedNpis.length > 1 ? "s" : ""} detected</p>}
                  </div>
                ) : (
                  <div className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer ${file ? "border-teal bg-teal/5" : "border-slate-300 hover:border-teal/50 bg-slate-50"}`}
                    onClick={() => fileRef.current?.click()} onDragOver={e => e.preventDefault()} onDrop={handleFileDrop}>
                    <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFileDrop} />
                    {file ? (
                      <div className="flex flex-col items-center gap-2">
                        <FileText className="w-10 h-10 text-teal" weight="fill" />
                        <p className="font-medium text-slate-900">{file.name}</p>
                        <button onClick={e => { e.stopPropagation(); setFile(null); }} className="text-sm text-red-500 hover:text-red-600 font-medium flex items-center gap-1"><Trash className="w-4 h-4" /> Remove</button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <UploadSimple className="w-10 h-10 text-slate-400" />
                        <p className="font-medium text-slate-900">Drop CSV here or click to browse</p>
                        <p className="text-sm text-slate-500">Max 5,000 NPIs</p>
                      </div>
                    )}
                  </div>
                )}

                {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                <Button onClick={handleSubmit} disabled={(inputMode === "upload" && !file) || (inputMode === "paste" && pastedNpis.length === 0) || uploading}
                  className="w-full h-12 bg-teal hover:bg-teal/90 text-white font-semibold rounded-xl text-base" data-testid="start-screening-btn">
                  {uploading ? <><CircleNotch className="w-5 h-5 animate-spin mr-2" /> Processing...</>
                    : <><ShieldCheck className="w-5 h-5 mr-2" weight="fill" /> Start Screening{pastedNpis.length > 0 && inputMode === "paste" ? ` (${pastedNpis.length} NPIs)` : ""}</>}
                </Button>
              </div>
            )}

            {/* Active Job */}
            {jobId && (
              <div>
                {/* Progress bar */}
                {isProcessing && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CircleNotch className="w-5 h-5 text-teal animate-spin" />
                        <span className="text-sm font-medium text-slate-900">Screening in progress...</span>
                      </div>
                      <span className="text-sm text-slate-500">{jobMeta?.processed || 0} / {jobMeta?.total || 0}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-teal rounded-full transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    {jobMeta?.invalid_count > 0 && (
                      <p className="text-xs text-amber-600 mt-2">{jobMeta.invalid_count} invalid entries skipped, {jobMeta.dupe_count || 0} duplicates removed</p>
                    )}
                  </div>
                )}

                {/* Summary Cards */}
                {summary && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                    <SummaryCard label="Screened" value={summary.total} icon={Users} color="text-slate-700" bg="bg-slate-100" />
                    <SummaryCard label="Active" value={summary.active} icon={CheckCircle} color="text-emerald-600" bg="bg-emerald-50" />
                    <SummaryCard label="Inactive / Error" value={summary.inactive} icon={Warning} color="text-amber-600" bg="bg-amber-50" />
                    <SummaryCard label="Medicare" value={summary.pecos_enrolled} icon={ShieldCheck} color="text-teal" bg="bg-teal/10" />
                    <SummaryCard label="High Risk" value={summary.high_risk} icon={ShieldWarning} color="text-red-600" bg="bg-red-50" />
                    <SummaryCard label="Changed (30d)" value={summary.changed_recently} icon={Pulse} color="text-violet-600" bg="bg-violet-50" />
                  </div>
                )}

                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" placeholder="Search NPI, name, city..." value={search}
                      onChange={e => { setSearch(e.target.value); setPage(1); }}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal/30" />
                  </div>
                  <button onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${showFilters || hasActiveFilters ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                    <Funnel className="w-4 h-4" /> Filters {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-teal" />}
                  </button>
                  <div className="flex items-center gap-2 ml-auto">
                    <a href={`${API_URL}/api/bulk-screening/download/${jobId}${riskFilter === "high" ? "?filter_risk=high" : ""}`} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm" className="gap-2 rounded-lg" disabled={!isComplete}>
                        <DownloadSimple className="w-4 h-4" /> Export CSV
                      </Button>
                    </a>
                    <Button onClick={handleReset} variant="outline" size="sm" className="gap-2 rounded-lg text-slate-500">
                      <X className="w-4 h-4" /> New Screening
                    </Button>
                  </div>
                </div>

                {/* Filters */}
                {showFilters && (
                  <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap items-end gap-3">
                    <FilterSelect label="Status" value={statusFilter} onChange={v => { setStatusFilter(v); setPage(1); }}
                      options={[["all","All"], ["active","Active"], ["inactive","Inactive"], ["deactivated","Deactivated"], ["not found","Not Found"]]} />
                    <FilterSelect label="Entity" value={entityFilter} onChange={v => { setEntityFilter(v); setPage(1); }}
                      options={[["all","All"], ["individual","Individual"], ["organization","Organization"]]} />
                    <FilterSelect label="PECOS" value={pecosFilter} onChange={v => { setPecosFilter(v); setPage(1); }}
                      options={[["all","All"], ["enrolled","Enrolled"], ["not_enrolled","Not Enrolled"]]} />
                    <FilterSelect label="Risk" value={riskFilter} onChange={v => { setRiskFilter(v); setPage(1); }}
                      options={[["all","All"], ["high","Has Flags"], ["clean","Clean"]]} />
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">State</label>
                      <input type="text" value={stateFilter} onChange={e => { setStateFilter(e.target.value.toUpperCase().slice(0, 2)); setPage(1); }}
                        placeholder="e.g. CA" maxLength={2} className="w-16 text-sm border border-slate-200 rounded-lg px-2 py-1.5 uppercase" />
                    </div>
                    {hasActiveFilters && (
                      <button onClick={() => { setSearch(""); setStatusFilter("all"); setEntityFilter("all"); setStateFilter(""); setPecosFilter("all"); setRiskFilter("all"); setPage(1); }}
                        className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 pb-1"><X className="w-3 h-3" /> Clear all</button>
                    )}
                  </div>
                )}

                {/* Results count + per-page */}
                <div className="flex items-center justify-between mb-2 text-sm text-slate-500">
                  <span>{totalFiltered} result{totalFiltered !== 1 ? "s" : ""}{hasActiveFilters ? " (filtered)" : ""}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">Rows:</span>
                    <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                      className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white">
                      {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>

                {/* Results Table */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
                  {results.length === 0 && isComplete ? (
                    <div className="p-12 text-center text-sm text-slate-400">{hasActiveFilters ? "No results match filters" : "No results"}</div>
                  ) : results.length === 0 && isProcessing ? (
                    <div className="p-12 text-center"><CircleNotch className="w-6 h-6 text-teal animate-spin mx-auto mb-2" /><p className="text-sm text-slate-500">Loading results...</p></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[13px]">
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-slate-50 border-b border-slate-200">
                            {columns.map(c => (
                              <th key={c.key} onClick={() => c.sortable && toggleSort(c.key)}
                                className={`text-left px-3 py-2.5 font-semibold text-slate-600 whitespace-nowrap ${c.sortable ? "cursor-pointer hover:text-slate-900 select-none" : ""} ${c.className || ""}`}>
                                <span className="flex items-center gap-1">
                                  {c.label}
                                  {c.sortable && sortBy === c.key && (sortDir === "asc" ? <CaretUp className="w-3 h-3" /> : <CaretDown className="w-3 h-3" />)}
                                </span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {results.map((r, i) => (
                            <tr key={r.npi + i} className={`border-b border-slate-100 hover:bg-slate-50/50 ${r.risk_flags?.length > 0 ? "bg-red-50/30" : ""}`}>
                              <td className="px-3 py-2.5 font-mono text-slate-700">{r.npi}</td>
                              <td className="px-3 py-2.5"><StatusBadge status={r.status} /></td>
                              <td className="px-3 py-2.5 text-slate-600">{r.entity_type === "Individual" ? "Ind" : r.entity_type === "Organization" ? "Org" : "—"}</td>
                              <td className="px-3 py-2.5 font-medium text-slate-900 max-w-[200px] truncate">{r.provider_name || "—"}</td>
                              <td className="px-3 py-2.5 text-slate-600 max-w-[160px] truncate">{r.primary_specialty || "—"}</td>
                              <td className="px-3 py-2.5 text-slate-500">{r.city || "—"}</td>
                              <td className="px-3 py-2.5 text-slate-500">{r.state || "—"}</td>
                              <td className="px-3 py-2.5 text-center"><PecosBadge enrolled={r.pecos_enrolled} /></td>
                              <td className="px-3 py-2.5"><RiskFlags flags={r.risk_flags} excluded={r.oig_excluded} /></td>
                              <td className="px-3 py-2.5 text-center">{r.changed_recently ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-violet-50 text-violet-700 text-[11px] font-semibold rounded-full">Changed</span> : <span className="text-slate-400">—</span>}</td>
                              <td className="px-3 py-2.5 text-right">
                                <Link to={`/provider/${r.npi}`} className="text-teal hover:text-teal/80" title="View Profile"><Eye className="w-4 h-4 inline" /></Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <span>Page {page} of {totalPages}</span>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)} className="rounded-lg px-2 h-8">1</Button>
                      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-lg gap-1 h-8"><ArrowLeft className="w-3 h-3" /></Button>
                      <span className="px-2 text-sm font-medium text-slate-700">{page}</span>
                      <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-lg gap-1 h-8"><ArrowRight className="w-3 h-3" /></Button>
                      <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="rounded-lg px-2 h-8">{totalPages}</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
          )}
        </section>

        {/* How It Works */}
        <section className="bg-slate-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display font-bold text-2xl text-slate-900 text-center mb-8">How It Works</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-navy/10 flex items-center justify-center mx-auto mb-4">
                  <UploadSimple className="w-6 h-6 text-navy" />
                </div>
                <h3 className="font-display font-semibold text-base text-slate-900 mb-2">1. Paste or Upload NPIs</h3>
                <p className="text-sm text-slate-500">Paste NPI numbers directly or upload a CSV. We auto-detect the NPI column and validate every entry.</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-6 h-6 text-teal" weight="fill" />
                </div>
                <h3 className="font-display font-semibold text-base text-slate-900 mb-2">2. Automated Screening</h3>
                <p className="text-sm text-slate-500">Each NPI is checked against NPPES, PECOS (Medicare enrollment), and the OIG exclusion database with risk flag analysis.</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-4">
                  <DownloadSimple className="w-6 h-6 text-teal" />
                </div>
                <h3 className="font-display font-semibold text-base text-slate-900 mb-2">3. Review & Download</h3>
                <p className="text-sm text-slate-500">View enriched results on screen with filters and risk flags. Export a full compliance report as CSV.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display font-bold text-2xl text-slate-900 text-center mb-8">Frequently Asked Questions</h2>
            <div className="space-y-3">
              <FaqItem q="What format should my CSV be in?" a="Your CSV should have a column with 10-digit NPI numbers. We auto-detect columns named 'NPI'. Alternatively, paste NPI numbers directly — one per line, comma-separated, or space-separated." />
              <FaqItem q="How many NPIs can I screen at once?" a="Pro plans can screen up to 100 NPIs per batch. Team plans support up to 1,000 NPIs. Enterprise plans can screen up to 5,000 NPIs per batch." />
              <FaqItem q="What does the report include?" a="Each NPI is enriched with: provider name, specialty, address, entity type, NPI status, PECOS Medicare enrollment status, OIG exclusion check, risk flags (deactivated, exclusion, PECOS issues, missing data), and change indicators." />
              <FaqItem q="How long does screening take?" a="Most batches complete within seconds. Larger batches (500+ NPIs) may take 1-2 minutes as we check multiple government data sources for each provider." />
              <FaqItem q="Where does the data come from?" a={<>Data is sourced from the <a href="https://npiregistry.cms.hhs.gov/" target="_blank" rel="noreferrer" className="text-teal font-medium hover:underline">CMS NPPES NPI Registry</a>, <a href="https://data.cms.gov/provider-characteristics/medicare-provider-supplier-enrollment/medicare-fee-for-service-public-provider-enrollment" target="_blank" rel="noreferrer" className="text-teal font-medium hover:underline">PECOS Medicare Enrollment</a>, and the <a href="https://oig.hhs.gov/exclusions/" target="_blank" rel="noreferrer" className="text-teal font-medium hover:underline">HHS OIG LEIE Exclusion Database</a>.</>} />
              <FaqItem q="Can I save results for later?" a="Yes. You can export results as CSV at any time. Results are also available in your dashboard under Bulk Lookup history." />
            </div>
          </div>
        </section>

        {/* Official NPI Resource */}
        <section className="bg-slate-50 py-8">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-sm text-slate-500">
              NPI data sourced from the official{" "}
              <a href="https://npiregistry.cms.hhs.gov/" target="_blank" rel="noreferrer" className="text-teal font-medium hover:underline">CMS NPPES NPI Registry</a>.
              For NPI application or updates, visit the{" "}
              <a href="https://nppes.cms.hhs.gov/" target="_blank" rel="noreferrer" className="text-teal font-medium hover:underline">NPPES portal</a>.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

// ── Table Columns ──────────────────────────────────

const columns = [
  { key: "npi", label: "NPI", sortable: true },
  { key: "status", label: "Status", sortable: true },
  { key: "entity_type", label: "Type", sortable: true },
  { key: "provider_name", label: "Provider Name", sortable: true },
  { key: "primary_specialty", label: "Specialty", sortable: true },
  { key: "city", label: "City", sortable: true },
  { key: "state", label: "State", sortable: true },
  { key: "pecos", label: "PECOS", sortable: false, className: "text-center" },
  { key: "risk", label: "Risk Flags", sortable: true },
  { key: "changed", label: "Changed", sortable: false, className: "text-center" },
  { key: "actions", label: "", sortable: false, className: "text-right" },
];

// ── Sub Components ─────────────────────────────────

function SummaryCard({ label, value, icon: Icon, color, bg }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2.5">
        <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
          <Icon className={`w-4 h-4 ${color}`} weight="fill" />
        </div>
        <div>
          <p className="text-xl font-bold text-slate-900">{value ?? 0}</p>
          <p className="text-[11px] text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    "Active": "bg-emerald-50 text-emerald-700",
    "Inactive": "bg-amber-50 text-amber-700",
    "Deactivated": "bg-red-50 text-red-700",
    "Not Found": "bg-slate-100 text-slate-500",
    "Error": "bg-slate-100 text-slate-400",
  };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${map[status] || map["Error"]}`}>{status}</span>;
}

function PecosBadge({ enrolled }) {
  if (enrolled === null || enrolled === undefined) return <span className="text-[11px] text-slate-400">N/A</span>;
  return enrolled
    ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-semibold rounded-full"><CheckCircle className="w-3 h-3" weight="fill" />Yes</span>
    : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 text-slate-500 text-[11px] font-semibold rounded-full">No</span>;
}

function RiskFlags({ flags, excluded }) {
  if (excluded) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-[11px] font-bold rounded-full"><ShieldWarning className="w-3 h-3" weight="fill" />EXCLUDED</span>;
  }
  if (!flags || flags.length === 0) {
    return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-600 text-[11px] font-semibold rounded-full"><CheckCircle className="w-3 h-3" weight="fill" />Clean</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {flags.slice(0, 2).map((f, i) => (
        <span key={i} className="inline-flex px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-semibold rounded-full whitespace-nowrap">{f}</span>
      ))}
      {flags.length > 2 && <span className="text-[10px] text-amber-600 font-semibold">+{flags.length - 2}</span>}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white min-w-[100px]">
        {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors">
        <span className="font-display font-semibold text-sm text-slate-900">{q}</span>
        <ArrowRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
}

export default BulkScreening;
